import React, { useState, useEffect, useCallback, useRef } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Label } from "@shadcn-in-nx/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shadcn-in-nx/ui/alert-dialog";
import { Loader2, CreditCard, CheckCircle, AlertCircle, Search } from "lucide-react";
import CompanyDetails from "./CompanyDetails";
import CreditLogs from "./CreditLogs";

export default function AdminCredits() {
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [credits, setCredits] = useState("");
    const [reason, setReason] = useState("");
    const [search, setSearch] = useState("");
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showConfirm, setShowConfirm] = useState(false);

    const { call: fetchCompanies, isLoading: loadingCompanies } = useBluelagoon({ method: "getCompaniesForCredits" });
    const { call: addCredits, isLoading: submitting } = useBluelagoon({ method: "addAdminCredits" });
    const addCreditsRef = useRef(addCredits);
    addCreditsRef.current = addCredits;

    const selectedCompanyData = companies.find(c => c.id === selectedCompany);

    const loadCompanies = useCallback(async (searchTerm) => {
        try {
            const response = await fetchCompanies({ limit: 100, search: searchTerm || undefined });
            if (response?.data) setCompanies(response.data);
        } catch (err) {
            console.error("Failed to fetch companies:", err);
        }
    }, [fetchCompanies]);

    useEffect(() => {
        const timer = setTimeout(() => loadCompanies(search), 300);
        return () => clearTimeout(timer);
    }, [search, loadCompanies]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setSuccess(null);
        setError(null);
        if (!selectedCompany || !credits || !reason.trim()) {
            setError("All fields are required");
            return;
        }
        setShowConfirm(true);
    };

    const handleConfirm = useCallback(async () => {
        setShowConfirm(false);
        try {
            const response = await addCreditsRef.current({
                companyId: selectedCompany,
                credits: parseFloat(credits),
                reason: reason.trim(),
            });
            if (response?.success) {
                setSuccess(`Added ${credits} credits to ${response.data.companyName}`);
                setCredits("");
                setReason("");
                loadCompanies(search);
                setRefreshKey(k => k + 1);
            }
        } catch (err) {
            setError(err?.error || "Failed to add credits");
        }
    }, [selectedCompany, credits, reason, loadCompanies, search]);

    return (
        <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <CreditCard className="h-5 w-5" />
                            Add Company Credits
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Search Company (by name or user email)</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Company</Label>
                                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={loadingCompanies ? "Loading..." : "Select company"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map((company) => (
                                            <SelectItem key={company.id} value={company.id}>
                                                {company.name} ({company.currentCredits.toFixed(2)} credits)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Credits to Add</Label>
                                <Input type="number" min="1" step="1" value={credits} onChange={(e) => setCredits(e.target.value)} placeholder="Enter amount" />
                            </div>
                            <div className="space-y-2">
                                <Label>Reason</Label>
                                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter reason for adding credits" rows={3} />
                            </div>
                            {error && <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="h-4 w-4" />{error}</div>}
                            {success && <div className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle className="h-4 w-4" />{success}</div>}
                            <Button type="submit" disabled={submitting || !selectedCompany || !credits || !reason.trim()}>
                                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Add Credits
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {selectedCompany && <CompanyDetails key={`${selectedCompany}-${refreshKey}`} companyId={selectedCompany} onCompanyDeleted={() => { setSelectedCompany(""); loadCompanies(search); }} />}
            </div>

            {selectedCompany && <CreditLogs key={`logs-${selectedCompany}-${refreshKey}`} companyId={selectedCompany} />}

            <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Credit Addition</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <p>You are about to add <strong>{credits}</strong> credits to:</p>
                            <p className="font-medium text-foreground">{selectedCompanyData?.name}</p>
                            <p className="text-sm">Reason: {reason}</p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirm}>Confirm</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
